<template>
  <div>

    <b-modal v-model="actionModal"
             lazy
             :title="actionTypeTitle"
             @ok="performActionOnCurrentSelection"
             :ok-disabled="modalActionDisabled">

      <template #modal-footer="{ ok, cancel, hide }">
        <div class="flex-grow-1"></div>

        <b-button size="sm" variant="secondary" @click="cancel()">
          Cancel
        </b-button>

        <b-button size="sm" variant="success" @click="ok()" :disabled="modalActionDisabled">
          {{ actionTypeButtonTitle }}
        </b-button>

      </template>

      <div v-if="actionType === 'add'">
        <h6>Select the categories to add to {{editingSelectionIds.length}} assets</h6>

        <tag-select field-name="Categories"
                    :tags="categories"
                    :multiple="true"
                    v-model="selectedCategories"/>
      </div>

    </b-modal>

    <b-modal v-model="deleteConfirmModal"
             lazy
             @ok="deleteCurrentSelection">
      <h6>Delete <code>{{ editingCategory.name }}</code> from {{ editingSelectionIds.length }} assets?</h6>
    </b-modal>

    <div class="d-flex justify-content-between mb-2">
      <b-checkbox v-model="showAssetNumber">Show Asset Numbers</b-checkbox>

    </div>

    <div class="card mb-3 no-shadow">
      <div class="card-body p-2">
        <h6 class="font-size-sm">All Categories from all present assets</h6>
        <div class="d-flex flex-wrap align-items-center">
          <template v-for="(data, categoryId) in groupedCategories">
            <div class="cursor-pointer border rounded mr-2 mb-1 p-1"
                 :class="{'bg-light-green': editing === data.category.id}">

              <div @click="toggleEditing(data.category.id)">
                {{ data.category.name }}
                <b-badge v-if="showAssetNumber" variant="primary" title="assets">
                  {{ data.assets.length }}
                </b-badge>
              </div>

            </div>
          </template>

        </div>
      </div>
    </div>

    <div class="card mb-3 no-shadow" v-if="currentAssetSelection.length">
      <div class="card-body p-2">
        <h6 class="font-size-sm">Categories in <b>below selection</b> of assets</h6>
        <div class="d-flex flex-wrap align-items-center">
          <template v-for="(data, categoryId) in groupedSelectedAssetCategories">
            <div class=" border rounded mr-2 mb-1 p-1">

              <div>
                {{ data.category.name }}
                <b-badge v-if="showAssetNumber" variant="primary" title="assets">
                  {{ data.assets.length }}
                </b-badge>
                <i class="cursor-pointer fas fa-trash text-danger"
                   @click="deleteCategoryFromAssets(data.category.id, data.assets)"
                   v-b-tooltip :title="`Remove ${data.category.name} from ${data.assets.length} assets`"/>
              </div>

            </div>
          </template>

        </div>
      </div>
    </div>

    <!--  Asset with thumbnails / checkboxes list  -->
    <div class="card mb-3 no-shadow">
      <div class="card-body p-2">
        <h6 v-if="editing">{{ editingCategory.name }}</h6>
        <div class="mb-2 d-flex align-items-start" style="gap: 5px">
          <b-button size="sm"
                    :variant="editingSelectionSelectAllButtonVariant('all')"
                    @click="editingSelectionSelectAll(true)">
            Select All
          </b-button>

          <b-button size="sm"
                    :variant="editingSelectionSelectAllButtonVariant('none')"
                    @click="editingSelectionSelectAll(false)">
            Unselect All
          </b-button>

          <div class="flex-grow-1"></div>

          <div>
            <div v-if="editing">
              <b-button variant="danger"
                        size="sm"
                        :disabled="editingSelectionIds.length === 0"
                        @click.stop="deleteConfirmModal = true">
                Delete from Selection
              </b-button>

              <b-button variant="info"
                        size="sm"
                        :disabled="editingSelectionIds.length === 0"
                        @click="prepareActionType('add', editingCategoryObj)">
                Add Categories to Selection
              </b-button>

              <b-button variant="primary"
                        size="sm"
                        :disabled="editingSelectionIds.length === 0"
                        @click="saveCategoryOnSelection">
                Save {{ editingCategory.name }} to Selection
              </b-button>
            </div>
            <div v-else class="d-flex justify-content-end">
              <b-button :disabled="editingSelectionIds.length === 0" variant="primary" size="sm" @click="prepareGlobalAdd()">
                Add Categories
              </b-button>
            </div>


            <div class="font-size-sm font-italic text-gray-700 text-right">Assets with <span
                class="text-danger">red</span> border are already at the max 3 categories
            </div>
          </div>
        </div>

        <div class="d-flex flex-wrap align-items-center">
          <div :class="{'border-danger': asset.category_ids.length === 3}"
               class="cursor-pointer border rounded mr-2 mb-1 p-1" v-for="asset in selectedLoadedAssets">
            <b-checkbox v-model="editingSelection[asset.id]">
              <div class="img-bg img-bg-1 img-square" :style="`background-image: url('${asset.thumb_url}')`"></div>
            </b-checkbox>
          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script>

import GeneralMixins from "../../../../mixins/GeneralMixins";
import PulseLoader from 'vue-spinner/src/PulseLoader';
import TagSelect from "../../../Common/TagSelect";
import union from 'lodash/union';
import normalize from "json-api-normalize";
import Attributes from "../../../../models/Attributes.js";
import {isEqual} from "lodash";

export default {
  props: ['selectedAssets', 'idKey', 'categories'],
  components: {PulseLoader, TagSelect},
  mixins: [GeneralMixins],

  data() {
    return {
      editing: null,
      editingSelection: {},
      actionModal: false,
      deleteConfirmModal: false,
      actionType: null,
      showAssetNumber: true,
      bulkActionRunning: false,
      selectedCategories: [],
      loaded: false,
      selectedLoadedAssets: []
    }
  },

  mounted() {
    if (this.idKey === "id") {
      this.loaded = true
      this.selectedLoadedAssets = this.selectedAssets
    } else {
      const assetIds = this.selectedAssets.map(el => el[this.idKey])

      this.$http.post("/curators/bulk/asset_loader", {asset_ids: assetIds}).then(result => {
        this.selectedLoadedAssets = normalize(result.body).get(Attributes.assetWithIncludesAttributes);
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      })
    }
  },

  methods: {
    toggleEditing(categoryId) {
      if (this.editing !== categoryId) {
        this.startEditing(categoryId)
      } else {
        this.stopEditing()
      }
    },

    startEditing(categoryId) {
      this.editing = categoryId
      this.editingValue = categoryId
      this.editingSelection = Object.assign({}, ...this.selectedLoadedAssets.map(asset => {
        return {
          [asset.id]: this.assetContainsCategoryId(asset.id, categoryId)
        }
      }))
    },

    assetContainsCategoryId(assetId, categoryId) {
      return this.selectedLoadedAssets.find(el => el.id === assetId).category_ids.includes(categoryId)
    },

    newCategoriesOnSelection(categoryIds, selectionIds) {
      const attributes = {
        asset_ids: selectionIds,
        category_ids: categoryIds,
      }

      this.bulkActionRunning = true

      this.$http.post("/curators/bulk/asset_categories/add_categories", attributes).then(result => {
        let assets = result.body.assets
        let count = assets.length
        this.showToast(`The categories were added to ${count} assets`, "Success")

        this.selectedLoadedAssets.forEach(asset => {
          let resultAsset = assets.filter(img => img.id === asset.id)[0]
          if (resultAsset) {
            asset.category_ids = resultAsset.category_ids
          }
        })

        // this.$emit('change', this.selectedLoadedAssets)
        this.actionModal = false
        this.stopEditing()
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(result => {
        this.bulkActionRunning = false
      });
    },

    saveCategoryOnSelection() {
      this.newCategoriesOnSelection([this.editing], this.editingSelectionIds)
    },

    deleteCategoryFromAssets(categoryId, assets){
      const cat = this.findCategory(categoryId)

      if (confirm(`Are you sure you want to remove ${cat.name} from ${assets.length} assets?`)) {
        this.deleteCategorySelection([categoryId], assets.map(el => el.id))
      }
    },

    deleteCurrentSelection() {
      this.deleteCategorySelection([this.editing], this.editingSelectionIds)
    },

    deleteCategorySelection(categoryIds, assetIds) {
      const attributes = {
        asset_ids: assetIds,
        category_ids: categoryIds
      }

      this.bulkActionRunning = true

      this.$http.post("/curators/bulk/asset_categories/delete_categories", attributes).then(result => {

        let assets = result.body.assets
        let count = assets.length
        this.showToast(`The categories were removed from ${count} assets`, "Success")

        this.selectedLoadedAssets.forEach(asset => {
          let resultAsset = assets.filter(img => img.id === asset.id)[0]
          if (resultAsset) {
            asset.category_ids = resultAsset.category_ids
          }
        })

        // this.$emit('change', this.selectedLoadedAssets)
        this.actionModal = false
        this.stopEditing()
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(result => {
        this.bulkActionRunning = false
      });
    },

    stopEditing() {
      this.editing = null
      this.editingValue = null
      this.actionType = null
      this.createBlankCategorySelection()
    },

    // obj is generated by groupedCategories()
    prepareActionType(actionType, obj) {
      this.createBlankCategorySelection()
      this.actionType = actionType
      if (!this.editingSelection) {
        this.editingSelection = Object.assign({}, ...this.selectedLoadedAssets.map(asset => {
          return {
            [asset.id]: true
          }
        }))
      }

      this.actionModal = true
    },

    prepareGlobalAdd() {
      this.createBlankCategorySelection()

      this.actionType = 'add'
      this.editingSelection = Object.assign({}, ...this.editingSelectionIds.map(id => {
        return {
          [id]: true
        }
      }))
      this.actionModal = true
      this.editing = null
    },

    createBlankCategorySelection() {
      this.selectedCategories = []
    },

    findCategory(categoryId) {
      return this.categories.find(category => category.id === categoryId)
    },

    editingSelectionSelectAllButtonVariant(type) {
      if (type === 'all') {
        if (this.editingSelectionAllSelected) {
          console.log('all selected');
          return "primary"
        } else {
          return "outline-secondary"
        }
      } else {
        if (this.editingSelectionNoneSelected) {
          return "primary"
        } else {
          return "outline-secondary"
        }
      }
    },

    editingSelectionSelectAll(value) {
      this.editingSelection = Object.assign({}, ...this.selectedLoadedAssets.map(asset => {
        return {
          [asset.id]: value
        }
      }))
    },



    performActionOnCurrentSelection(evt) {
      evt.preventDefault()

      if (this.actionType === "delete") {
        this.deleteCurrentSelection()
      } else if (this.actionType === "add") {
        this.newCategoriesOnSelection(this.selectedCategories, this.editingSelectionIds)
      }
    },

    allAssetCategoryIds(assets) {
      const categoryArrays = assets.map(el => el.category_ids).filter(el => el)
      const categoryList = [].concat.apply([], categoryArrays)
      return [...new Set(categoryList)]
    },

    groupCategories(categoryIds, assets) {
      return categoryIds.reduce((obj, categoryId) => {
        obj[categoryId] = {
          assets: assets.filter(el => el.category_ids.includes(categoryId)),
          category: this.findCategory(categoryId)
        }
        return obj
      }, {})
    },
  },

  computed: {
    editingSelectionAllSelected() {
      if (!this.editingSelection) {
        return false
      }

      return this.editingSelectionIds.length === this.selectedLoadedAssets.length
    },

    editingSelectionNoneSelected() {
      if (!this.editingSelection) {
        return false
      }
      return this.editingSelectionIds.length === 0
    },
    editingCategory() {
      const category = this.findCategory(this.editing)
      if (category) {
        return category
      } else {
        return {}
      }
    },

    editingCategoryObj() {
      return this.groupedCategories[this.editing]
    },

    allUniqueCategoryIds() {
      return this.allAssetCategoryIds(this.selectedLoadedAssets)
    },

    groupedCategories() {
      return this.groupCategories(this.allUniqueCategoryIds, this.selectedLoadedAssets)
    },

    currentAssetSelection() {
      const assetIds = Object.keys(this.editingSelection).filter(k => this.editingSelection[k])
      return this.selectedLoadedAssets.filter(asset => assetIds.includes(asset.id))
    },

    groupedSelectedAssetCategories() {
      const assets = this.currentAssetSelection
      const categoryIds = this.allAssetCategoryIds(assets)
      return this.groupCategories(categoryIds, assets)
    },

    currentSelectedCategories() {
      return this.allUniqueCategoryIds.map(id => this.findCategory(id))
    },

    actionTypeTitle() {
      if (this.actionType === "delete") {
        return `Delete Category: ${this.editingCategory.name}`
      } else if (this.actionType === "add") {
        return `Add Categories`
      } else {
        return "Invalid Action"
      }
    },

    actionTypeButtonTitle() {
      if (this.actionType === "delete") {
        return `Delete`
      } else if (this.actionType === 'add') {
        return `Add`
      }
    },

    editingSelectionIds() {
      if (!this.editingSelection) {
        return []
      }

      return Object.keys(this.editingSelection).filter(id => this.editingSelection[id])
    },

    modalActionDisabled() {
      return this.editingSelectionIds.length === 0
    },
  }
}
</script>